import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BYPASS_AUTH } from 'src/app/_services/interceptors/http-config.interceptor';
import {
	AiSummaryConfig,
	AiSummaryConfigApi,
	AiSummaryConfigUpdatePayload,
	AiSummaryDetailResponse,
	AiSummaryDetailResponseApi,
	AiSummaryEntityType,
	AiSummaryListItem,
	AiSummaryListItemApi,
	AiSummaryListResponse,
	AiSummaryListResponseApi
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-ai-summary.model';
import { smiUrl } from 'components';
import { map, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class SocialListeningAiSummaryService {
	constructor(private http: HttpClient) {}

	list(
		entityType: AiSummaryEntityType,
		entityId: string
	): Observable<AiSummaryListResponse> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summaries')
			.query('entity_type', entityType)
			.query('entity_id', entityId)
			.done();

		return this.http
			.get<AiSummaryListResponseApi>(url)
			.pipe(map(response => this.mapListResponse(response)));
	}

	detail(summaryId: string): Observable<AiSummaryDetailResponse> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summaries')
			.add(summaryId)
			.done();

		return this.http
			.get<AiSummaryDetailResponseApi>(url)
			.pipe(map(response => this.mapDetailResponse(response)));
	}

	getConfig(configId: string): Observable<AiSummaryConfig> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summary-configs')
			.add(configId)
			.done();

		return this.http
			.get<AiSummaryConfigApi>(url)
			.pipe(map(response => this.mapConfig(response)));
	}

	delete(summaryId: string): Observable<void> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summaries')
			.add(summaryId)
			.done();

		return this.http.delete<void>(url);
	}

	updateConfig(
		configId: string,
		payload: AiSummaryConfigUpdatePayload
	): Observable<AiSummaryConfig> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summary-configs')
			.add(configId)
			.done();

		return this.http
			.put<AiSummaryConfigApi>(url, payload)
			.pipe(map(response => this.mapConfig(response)));
	}

	exportPdf(summaryId: string): Observable<Blob> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summaries')
			.add(summaryId)
			.add('exports')
			.add('pdf')
			.done();

		return this.http.post(url, null, { responseType: 'blob' });
	}

	getForExport(
		summaryId: string,
		exportToken: string
	): Observable<AiSummaryDetailResponse> {
		const url = smiUrl()
			.add('social-listening')
			.add('ai-summaries')
			.add(summaryId)
			.add('export-view')
			.query('export_token', exportToken)
			.done();

		return this.http
			.get<AiSummaryDetailResponseApi>(url, {
				context: new HttpContext().set(BYPASS_AUTH, true)
			})
			.pipe(map(response => this.mapDetailResponse(response)));
	}

	private mapConfig(response: AiSummaryConfigApi): AiSummaryConfig {
		return {
			id: response.id,
			language: response.language,
			period: response.period,
			frequency: response.frequency,
			scheduleDay: response.schedule_day,
			scheduleTime: response.schedule_time,
			scheduleTimezone: response.schedule_timezone,
			emailEnabled: response.email_enabled,
			emailRecipients: response.email_recipients
		};
	}

	private mapListResponse(
		response: AiSummaryListResponseApi
	): AiSummaryListResponse {
		return {
			bootstrapStatus: response.bootstrap_status,
			items: response.items.map(item => this.mapListItem(item))
		};
	}

	private mapListItem(item: AiSummaryListItemApi): AiSummaryListItem {
		return {
			id: item.id,
			configId: item.config_id,
			title: item.title,
			periodLabel: item.period_label,
			frequency: item.frequency,
			status: item.status,
			createdAt: item.created_at
		};
	}

	private mapDetailResponse(
		response: AiSummaryDetailResponseApi
	): AiSummaryDetailResponse {
		return {
			id: response.id,
			configId: response.config_id,
			title: response.title,
			status: response.status,
			periodFrom: response.period_from,
			periodTo: response.period_to,
			content: response.content,
			imageUrl: response.image_url,
			entityType: response.entity_type ?? 'brand'
		};
	}
}
